import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaFlask, FaLightbulb, FaCalculator } from 'react-icons/fa';

const IdealGasLawCalculator = () => {
  const [pressure, setPressure] = useState('');
  const [volume, setVolume] = useState('');
  const [temperature, setTemperature] = useState('');
  const [moles, setMoles] = useState('');
  const [showGraph, setShowGraph] = useState(false);
  const R = 8.314; // Universal gas constant in J/(mol·K)

  const calculateMissingValue = () => {
    const p = parseFloat(pressure);
    const v = parseFloat(volume);
    const t = parseFloat(temperature);
    const n = parseFloat(moles);

    if (pressure === '' && v && t && n) {
      const calculatedPressure = (n * R * t) / v;
      setPressure(calculatedPressure.toFixed(2));
    } else if (volume === '' && p && t && n) {
      const calculatedVolume = (n * R * t) / p;
      setVolume(calculatedVolume.toFixed(2));
    } else if (temperature === '' && p && v && n) {
      const calculatedTemperature = (p * v) / (n * R);
      setTemperature(calculatedTemperature.toFixed(2));
    } else if (moles === '' && p && v && t) {
      const calculatedMoles = (p * v) / (R * t);
      setMoles(calculatedMoles.toFixed(2));
    }

    setShowGraph(true);
  };

  const generateGraphData = () => {
    if (!pressure && !volume && !temperature && !moles) return null;

    const p = parseFloat(pressure);
    const v = parseFloat(volume);
    const t = parseFloat(temperature);
    const n = parseFloat(moles);

    // Generate data for volume vs temperature (isobaric process)
    const series = [
      {
        name: 'Volume vs Temperature',
        data: Array.from({ length: 10 }, (_, i) => {
          const currentTemperature = t + (i - 5);
          const currentVolume = (n * R * currentTemperature) / p;
          return {
            x: currentTemperature,
            y: currentVolume
          };
        }),
      },
    ];

    const options = {
      chart: {
        type: 'line',
        height: 400,
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      xaxis: {
        type: 'numeric',
        title: {
          text: 'Temperature (K)',
        },
      },
      yaxis: {
        title: {
          text: 'Volume (m³)',
        },
      },
      title: {
        text: 'Volume vs Temperature (Isobaric Process)',
        align: 'center',
      },
      markers: {
        size: 4,
        colors: ['#FF4560'],
      },
    };

    return { series, options };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-red-100 to-pink-100">
      <Helmet>
        <title>Ideal Gas Law Calculator | Physics Tool</title>
        <meta name="description" content="Calculate ideal gas properties using PV = nRT. Interactive calculator with graph visualization for understanding gas behavior." />
        <meta name="keywords" content="ideal gas law calculator, physics calculator, thermodynamics calculator, chemistry calculator, gas properties calculator" />
        <meta property="og:title" content="Ideal Gas Law Calculator | Physics Tool" />
        <meta property="og:description" content="Calculate ideal gas properties using PV = nRT. Interactive calculator with graph visualization for understanding gas behavior." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Ideal Gas Law Calculator | Physics Tool" />
        <meta name="twitter:description" content="Calculate ideal gas properties using PV = nRT. Interactive calculator with graph visualization for understanding gas behavior." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/ideal-gas-law" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-red-600 mb-4">
            <FaFlask />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Ideal Gas Law Calculator</h1>
          <p className="text-xl text-gray-600">Calculate gas properties using PV = nRT</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Pressure (Pa)</label>
                <input
                  type="number"
                  value={pressure}
                  onChange={(e) => setPressure(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter pressure in Pascals"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Volume (m³)</label>
                <input
                  type="number"
                  value={volume}
                  onChange={(e) => setVolume(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter volume in cubic meters"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Temperature (K)</label>
                <input
                  type="number"
                  value={temperature}
                  onChange={(e) => setTemperature(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter temperature in Kelvin"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Number of Moles (n)</label>
                <input
                  type="number"
                  value={moles}
                  onChange={(e) => setMoles(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter number of moles"
                />
              </div>
              <div>
                <button
                  onClick={calculateMissingValue}
                  className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate Missing Value
                </button>
              </div>

              {pressure && volume && temperature && moles && (
                <div className="mt-4 p-4 bg-red-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-red-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-red-800">Results</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-red-800">Pressure = {pressure} Pa</p>
                        <p className="text-red-800">Volume = {volume} m³</p>
                        <p className="text-red-800">Temperature = {temperature} K</p>
                        <p className="text-red-800">Moles = {moles} mol</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-yellow-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      Using the ideal gas law:
                      <br />
                      PV = nRT
                      <br />
                      <br />
                      Where:
                      <br />
                      P = {pressure} Pa
                      <br />
                      V = {volume} m³
                      <br />
                      T = {temperature} K
                      <br />
                      n = {moles} mol
                      <br />
                      R = 8.314 J/(mol·K)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Volume vs Temperature</h2>
              <Chart
                options={generateGraphData().options}
                series={generateGraphData().series}
                type="line"
                height={400}
              />
              <p className="mt-4 text-gray-600 text-center">
                Shows how volume changes with temperature at constant pressure
              </p>
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">How to Use</h2>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter three of the four values (P, V, T, n)</li>
            <li>Leave the value you want to calculate empty</li>
            <li>Click "Calculate Missing Value" to get the result</li>
            <li>View the interactive graph showing volume vs temperature relationship</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Flexible Calculations</h3>
              <p>Calculate any missing value using the ideal gas law</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Interactive Graph</h3>
              <p>Visualize how volume changes with temperature</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
              <p>Ideal for chemistry and physics students</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Step-by-Step Solutions</h3>
              <p>See how calculations are performed</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-red-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is the Ideal Gas Law?</h3>
              <p className="text-gray-600">
                The ideal gas law describes the behavior of gases under various conditions. It states that the product of pressure and volume is proportional to the product of the number of moles and temperature.
              </p>
            </div>

            <div className="border-l-4 border-red-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What units should I use?</h3>
              <p className="text-gray-600">
                For accurate calculations:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Pressure should be in Pascals (Pa)</li>
                  <li>Volume should be in cubic meters (m³)</li>
                  <li>Temperature should be in Kelvin (K)</li>
                  <li>Number of moles should be in moles (mol)</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-red-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">How does the graph work?</h3>
              <p className="text-gray-600">
                The graph shows how volume changes with temperature at constant pressure:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>X-axis represents temperature (K)</li>
                  <li>Y-axis represents volume (m³)</li>
                  <li>The line shows the linear relationship between temperature and volume</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-red-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are some real-world applications?</h3>
              <p className="text-gray-600">
                The ideal gas law is important in many areas:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Chemical engineering</li>
                  <li>Thermodynamics</li>
                  <li>Gas storage and transportation</li>
                  <li>Weather forecasting</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-red-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is this calculator useful?</h3>
              <p className="text-gray-600">
                This calculator helps:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Students understand gas behavior</li>
                  <li>Engineers calculate gas properties</li>
                  <li>Teachers demonstrate thermodynamics principles</li>
                  <li>Anyone needing to calculate gas properties</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdealGasLawCalculator;
